import {
  disableAutoPublishHeader,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { getOttEndpointListUrl } from "src/queries/playOttEndpoints"
import { getPushEndpointListUrl } from "src/queries/playPushEndpoints"
import { getPoolUrl } from "./playPools"
import type { OrgParams, PoolParams, ProcessingParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /processings/?org=${orgUuid} */
export const getOrgProcessingListUrl = ({ orgUuid }: Required<OrgParams>) =>
  `/processings/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/processings/${processingUuid}/?org=${orgUuid} */
export const getProcessingUrl = ({
  orgUuid,
  poolUuid,
  processingUuid,
  dryRun
}: Required<ProcessingParams>) =>
  `/pools/${poolUuid}/processings/${processingUuid}/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

/** @returns /pools/${poolUuid}/processings/?org=${orgUuid} */
export const getProcessingListUrl = ({
  orgUuid,
  poolUuid,
  dryRun
}: Required<PoolParams & { dryRun?: boolean }>) =>
  `/pools/${poolUuid}/processings/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useOrgProcessingList = (params?: OrgParams) => {
  const mergedParams = useRouteParams(params)
  const orgListUrl = getOrgProcessingListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedProcessingList>("ott", orgListUrl)
}

export const useProcessingList = (params?: PoolParams & { dryRun?: boolean }) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getProcessingListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedProcessingList>("ott", listUrl)
}

export const useProcessing = (params?: ProcessingParams) => {
  const mergedParams = useRouteParams(params)
  const url = getProcessingUrl(mergedParams)

  return useGetQuery<api.ott.Processing>("ott", url)
}

export const useAddProcessing = (params?: PoolParams & { dryRun?: boolean }) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getProcessingListUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.Processing>
  type TResponse = api.ott.Processing

  const headers = disableAutoPublishHeader
  return usePostQuery<TRequest, TResponse>("ott", listUrl, [poolUrl], { headers })
}

export const useUpdateProcessing = (params?: ProcessingParams) => {
  const mergedParams = useRouteParams(params)
  const url = getProcessingUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.Processing>
  type TResponse = api.ott.Processing

  return usePatchQuery<TRequest, TResponse>("ott", url, [poolUrl])
}

export const useDeleteProcessing = (params?: ProcessingParams) => {
  const mergedParams = useRouteParams(params)
  const url = getProcessingUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)
  const pushEndpointListUrl = getPushEndpointListUrl(mergedParams)
  const ottEndpointListUrl = getOttEndpointListUrl(mergedParams)

  return useDeleteQuery("ott", url, [poolUrl, pushEndpointListUrl, ottEndpointListUrl])
}
